import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { GLYPH } from './glyphs';

export interface PagerProps {
  page: number;
  lastPage: number;
  onPageChange: (page: number) => void;
  className?: string;
}

type PagerItem = { kind: 'page'; page: number } | { kind: 'gap'; key: string };

/** Номера страниц как в прототипе: при >7 страниц — 1, 2, …, текущая ±1, …, две последние. */
export function buildPagerItems(page: number, lastPage: number): PagerItem[] {
  const items: PagerItem[] = [];
  for (let p = 1; p <= lastPage; p += 1) {
    if (lastPage > 7 && p > 2 && p < lastPage - 1 && Math.abs(p - page) > 1) {
      const previous = items[items.length - 1];
      if (previous?.kind !== 'gap') items.push({ kind: 'gap', key: `gap-${p}` });
      continue;
    }
    items.push({ kind: 'page', page: p });
  }
  return items;
}

const arrowClasses =
  'flex h-24 cursor-pointer items-center justify-center rounded-9 border border-line-strong bg-paper-pure px-8 text-[12px] text-ink-700 hover:bg-paper-deep disabled:cursor-not-allowed disabled:text-ink-400 disabled:hover:bg-paper-pure';

/** Пагинация (README §3 «Подвал»): «‹», номера, «›» — высота 24, radius 9, border line-strong, 12. */
export function Pager({ page, lastPage, onPageChange, className }: PagerProps) {
  const { t } = useTranslation('registry');
  const items = buildPagerItems(page, lastPage);
  return (
    <nav aria-label={t('pager.label')} className={cn('flex items-center gap-3', className)}>
      <button
        type="button"
        className={arrowClasses}
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        aria-label={t('pager.prev')}
      >
        <span aria-hidden="true">{GLYPH.prev}</span>
      </button>
      {items.map((item) =>
        item.kind === 'gap' ? (
          <span key={item.key} aria-hidden="true" className="px-4 text-[12px] text-ink-400">
            {GLYPH.ellipsis}
          </span>
        ) : (
          <button
            key={item.page}
            type="button"
            onClick={() => onPageChange(item.page)}
            aria-label={t('pager.page', { page: item.page })}
            aria-current={item.page === page ? 'page' : undefined}
            className={cn(
              'flex h-24 min-w-24 cursor-pointer items-center justify-center rounded-9 border px-6 text-[12px] tabular hover:bg-paper-deep',
              item.page === page
                ? 'border-line-strong bg-surface-sunken font-semibold text-ink-900'
                : 'border-transparent bg-transparent font-medium text-ink-600',
            )}
          >
            {item.page}
          </button>
        ),
      )}
      <button
        type="button"
        className={arrowClasses}
        disabled={page >= lastPage}
        onClick={() => onPageChange(page + 1)}
        aria-label={t('pager.next')}
      >
        <span aria-hidden="true">{GLYPH.next}</span>
      </button>
    </nav>
  );
}
